import { useState } from 'react';
import { API_BASE_URL } from '@/config';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  currentPlan: string;
  photosUsed: number;
  photoQuota: number;
}

const PLANS = [
  { id: 'starter', name: 'Starter', price: 'R$19,90', period: '/mês', quota: 1000, features: ['1.000 fotos', 'Estatísticas básicas', 'Sem anúncios'] },
  { id: 'explorer', name: 'Explorer', price: 'R$49,90', period: '/mês', quota: 10000, features: ['10.000 fotos', 'Estatísticas completas', 'Mapas offline', 'Suporte prioritário'], recommended: true },
  { id: 'premium', name: 'Premium', price: 'R$99,90', period: '/mês', quota: 999999, features: ['Fotos ilimitadas', 'Tudo do Explorer', 'API access', 'Backup automático', 'Suporte VIP'] },
];

export function UpgradeModal({ open, onClose, currentPlan, photosUsed, photoQuota }: UpgradeModalProps) {
  const [loading, setLoading] = useState<string | null>(null);

  const handleUpgrade = async (planId: string) => {
    setLoading(planId);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/api/v1/billing/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: planId }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, pt: 4 }}>
        Limite de fotos atingido
      </DialogTitle>
      <DialogContent>
        <Typography textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
          Você usou {photosUsed} de {photoQuota} fotos do plano {currentPlan}.
          Faça upgrade para continuar adicionando memórias!
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', pb: 3 }}>
          {PLANS.filter(p => p.id !== currentPlan).map((plan) => (
            <Card key={plan.id} sx={{ width: 250, border: plan.recommended ? '2px solid' : '1px solid', borderColor: plan.recommended ? 'primary.main' : 'grey.200', position: 'relative' }}>
              {plan.recommended && <Chip label="RECOMENDADO" size="small" color="primary" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }} />}
              <CardContent sx={{ textAlign: 'center', pt: plan.recommended ? 4 : 3 }}>
                <Typography variant="h6" fontWeight={600}>{plan.name}</Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">{plan.price}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{plan.period}</Typography>
                {plan.features.map(f => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2">{f}</Typography>
                  </Box>
                ))}
                <Button
                  variant={plan.recommended ? 'contained' : 'outlined'}
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={loading !== null}
                >
                  {loading === plan.id ? <CircularProgress size={20} /> : 'Assinar'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
